import { Search, X } from "lucide-react"
import type { ComponentProps, ReactNode } from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type ClearableSearchInputProps = Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange"
> & {
  value: string
  onChange: (next: string) => void
  /** Hide the leading search icon (default shown). */
  hideSearchIcon?: boolean
  /** Control at the end of the field (e.g. a filters icon). */
  trailing?: ReactNode
}

/**
 * Controlled search field with a clear (X) button once text is present.
 * Prefer {@link ModuleSearchField} for list toolbars; use this for one-off search UIs.
 */
export const ClearableSearchInput = ({
  value,
  onChange,
  className,
  hideSearchIcon = false,
  trailing,
  ...rest
}: ClearableSearchInputProps) => {
  const hasValue = value.length > 0
  const hasTrailing = trailing != null

  return (
    <div className="relative min-w-0 w-full">
      {!hideSearchIcon ? (
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
      ) : null}
      <Input
        {...rest}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          !hideSearchIcon && "pl-9",
          hasValue && hasTrailing && "pr-16",
          hasValue && !hasTrailing && "pr-9",
          !hasValue && hasTrailing && "pr-10",
          className
        )}
      />
      {hasValue ? (
        <button
          type="button"
          className={cn(
            "absolute top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            hasTrailing ? "right-8" : "right-2"
          )}
          aria-label="Clear search"
          onClick={() => onChange("")}
        >
          <X className="size-3.5" />
        </button>
      ) : null}
      {hasTrailing ? (
        <div className="absolute top-1/2 right-1 z-10 -translate-y-1/2">
          {trailing}
        </div>
      ) : null}
    </div>
  )
}
